import { Alert, Button, Drawer, Group, Skeleton } from '@mantine/core';
import {
  IconAdjustments,
  IconAlertTriangle,
  IconClipboardCheck,
  IconFirstAidKit,
  IconFlask,
  IconPackageExport,
  IconPackageImport,
  IconTrash,
} from '@tabler/icons-react';
import { type ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import { formatDateTime, formatDay } from '../../format.js';
import { StatusBadge, type StatusTone } from '../../StatusBadge.js';
import { StockStatusBadge } from '../materials/badges.js';
import { formatQuantity, formatSignedQuantity, formatUnit } from '../materials/inventory.js';
import classes from '../materials/Materials.module.css';
import css from './Provisions.module.css';
import type { ProvisionSource } from './source.js';
import type {
  ExpiryState,
  ProvisionItem,
  ProvisionLot,
  ProvisionMovement,
  ProvisionPermissions,
} from './types.js';

export type ProvisionAction = 'in' | 'out' | 'dispose' | 'count' | 'levels';

export const EXPIRY_TONE: Record<ExpiryState, StatusTone> = {
  expired: 'danger',
  expiring: 'warning',
  ok: 'success',
  none: 'neutral',
};

/** Son kullanma rozeti: durum ve tarih. */
export function ExpiryBadge({ state, date }: { state: ExpiryState; date: string | null }) {
  const { t } = useTranslation();
  if (state === 'none' || !date) {
    return <span className={classes.muted}>{date ? formatDay(date) : '—'}</span>;
  }
  if (state === 'ok') return <span>{formatDay(date)}</span>;
  return (
    <span title={t(`provisions.expiryState.${state}`)}>
      <StatusBadge tone={EXPIRY_TONE[state]}>{formatDay(date)}</StatusBadge>
    </span>
  );
}

/**
 * Kumanya kaleminin ayrıntısı (ADR 0099 §2, §14): kalem bilgileri (depolama, sıcaklık, alerjen
 * etiketleri ve "etiketle doğrulayın" uyarısı, diyet notu, tıbbi ve kimyasal notlar), lotlar ve
 * hareket geçmişi. Gemide işlem düğmeleri yetkiye göre; ofiste salt okunur.
 */
export interface ProvisionDetailProps {
  item: ProvisionItem | null;
  source: ProvisionSource;
  vesselId: string | null;
  can: ProvisionPermissions | null;
  /** Kayıttan sonra artar: lotlar ve geçmiş yeniden yüklenir. */
  version: number;
  /** Şirketin son kullanma uyarı penceresi (gün; ADR 0099 §9): partiler başlığında küçük metin. */
  expiryWarningDays?: number;
  onAction: (action: ProvisionAction, item: ProvisionItem, lotId?: string) => void;
  onClose: () => void;
}

export function ProvisionDetail(props: ProvisionDetailProps) {
  const { t } = useTranslation();
  const { item, onClose } = props;
  return (
    <Drawer
      opened={item !== null}
      onClose={onClose}
      position="right"
      size="xl"
      title={<span className={css.drawerTitle}>{item?.name}</span>}
      closeButtonProps={{ 'aria-label': t('provisions.detail.close') }}
    >
      {item ? <ProvisionDetailBody {...props} item={item} /> : null}
    </Drawer>
  );
}

/** Çekmecenin içi (testte çekmecesiz çizilir). */
export function ProvisionDetailBody({
  item,
  source,
  vesselId,
  can,
  version,
  expiryWarningDays,
  onAction,
}: ProvisionDetailProps & { item: ProvisionItem }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'en';
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`provisions.detail.${key}`, options);
  const [data, setData] = useState<{
    lots: ProvisionLot[];
    movements: ProvisionMovement[];
  } | null>(null);
  const [failed, setFailed] = useState(false);
  const stockItemId = item.stockItemId;

  useEffect(() => {
    void version;
    if (!stockItemId) {
      setData({ lots: [], movements: [] });
      return;
    }
    let alive = true;
    setFailed(false);
    Promise.all([
      source.lots({ stockItemId }, vesselId),
      source.movements({ stockItemId, limit: 50 }, vesselId),
    ])
      .then(([lots, movements]) => {
        if (alive) setData({ lots, movements });
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [source, vesselId, stockItemId, version]);

  const unit = formatUnit(item.unit);
  const qty = (value: number | null) =>
    value === null ? '—' : `${formatQuantity(value, locale)} ${unit}`;
  const editor = Boolean(source.editor);
  const onBoard = item.stockItemId !== null;
  const fact = (label: string, value: ReactNode) => (
    <div>
      <dt>{label}</dt>
      <dd>{value ?? '—'}</dd>
    </div>
  );
  const tracking = (mode: string | null) =>
    mode ? t(`provisions.tracking.${mode}`, { defaultValue: mode }) : '—';
  const medical = item.storageType === 'Medical' || item.costCenter === 'Medical';
  const chemical = item.storageType === 'Chemical';

  return (
    <div className={css.detail}>
      <div className={css.detailHead}>
        <span className={css.code}>{item.itemCode ?? '—'}</span>
        <span className={classes.muted}>
          {[item.mainCategory, item.subCategory].filter(Boolean).join(' › ')}
        </span>
        {item.status ? (
          <StockStatusBadge status={item.status} />
        ) : (
          <StatusBadge tone="neutral">{t('provisions.status.notOnBoard')}</StatusBadge>
        )}
        {item.active ? null : <StatusBadge tone="neutral">{k('inactive')}</StatusBadge>}
      </div>

      {editor && can ? (
        <Group gap="xs" className={css.detailActions}>
          {can.create && item.active ? (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconPackageImport size={15} />}
              onClick={() => onAction('in', item)}
            >
              {t('provisions.actions.stockIn')}
            </Button>
          ) : null}
          {can.create && onBoard ? (
            <Button
              size="xs"
              variant="light"
              color="info"
              leftSection={<IconPackageExport size={15} />}
              onClick={() => onAction('out', item)}
            >
              {t('provisions.actions.stockOut')}
            </Button>
          ) : null}
          {can.approve && onBoard ? (
            <Button
              size="xs"
              variant="light"
              color="red"
              leftSection={<IconTrash size={15} />}
              onClick={() => onAction('dispose', item)}
            >
              {t('provisions.actions.dispose')}
            </Button>
          ) : null}
          {can.edit && onBoard ? (
            <>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconClipboardCheck size={15} />}
                onClick={() => onAction('count', item)}
              >
                {t('provisions.actions.count')}
              </Button>
              <Button
                size="xs"
                variant="default"
                leftSection={<IconAdjustments size={15} />}
                onClick={() => onAction('levels', item)}
              >
                {k('editLevels')}
              </Button>
            </>
          ) : null}
        </Group>
      ) : null}

      <section aria-label={k('stock')}>
        <h3 className={css.detailTitle}>{k('stock')}</h3>
        <dl className={css.facts}>
          {fact(t('provisions.columns.onHand'), qty(item.onHand))}
          {fact(t('provisions.columns.min'), qty(item.minQty))}
          {fact(t('provisions.columns.reorder'), qty(item.reorderLevel))}
          {fact(t('provisions.columns.max'), qty(item.maxQty))}
          {fact(t('provisions.columns.location'), item.location)}
          {fact(
            t('provisions.columns.nextExpiry'),
            <ExpiryBadge state={item.expiryState} date={item.nextExpiry} />,
          )}
        </dl>
      </section>

      <section aria-label={k('info')}>
        <h3 className={css.detailTitle}>{k('info')}</h3>
        <dl className={css.facts}>
          {fact(
            t('provisions.columns.storage'),
            item.storageType
              ? t(`provisions.storageTypes.${item.storageType}`, {
                  defaultValue: item.storageType,
                })
              : null,
          )}
          {fact(t('provisions.columns.temperature'), item.temperatureGuidance)}
          {fact(t('provisions.columns.packSize'), item.packSize)}
          {fact(t('provisions.columns.unit'), unit)}
          {fact(
            t('provisions.columns.costCenter'),
            item.costCenter
              ? t(`provisions.costCenters.${item.costCenter}`, { defaultValue: item.costCenter })
              : null,
          )}
          {fact(k('batchTracking'), tracking(item.batchTracking))}
          {fact(k('expiryTracking'), tracking(item.expiryTracking))}
          {fact(k('fefo'), item.fefoRequired ? k('yes') : k('no'))}
          {fact(
            k('shelfLife'),
            item.shelfLifeDays === null ? null : k('days', { count: item.shelfLifeDays }),
          )}
          {fact(k('impaIssa'), [item.impaCode, item.issaCode].filter(Boolean).join(' / ') || null)}
        </dl>
      </section>

      <section aria-label={t('provisions.columns.allergens')}>
        <h3 className={css.detailTitle}>{t('provisions.columns.allergens')}</h3>
        {item.allergenTags === null ? (
          <p className={classes.muted}>{k('allergensUnknown')}</p>
        ) : item.allergenTags.length === 0 ? (
          <p>{k('allergensNone')}</p>
        ) : (
          <ul className={css.tags} aria-label={t('provisions.columns.allergens')}>
            {item.allergenTags.map((tag) => (
              <li key={tag}>
                <StatusBadge tone="warning">
                  {t(`provisions.allergens.${tag}`, { defaultValue: tag })}
                </StatusBadge>
              </li>
            ))}
          </ul>
        )}
        {item.allergenTags !== null ? (
          item.allergenVerified ? (
            <p className={css.verified}>{t('provisions.hints.allergenVerified')}</p>
          ) : (
            <Alert
              color="yellow"
              variant="light"
              role="note"
              icon={<IconAlertTriangle size={16} />}
              className={css.note}
            >
              {t('provisions.hints.allergenStarter')}
            </Alert>
          )
        ) : null}
        {item.dietaryTags && item.dietaryTags.length > 0 ? (
          <>
            <p className={css.dietary}>
              <strong>{k('dietary')}:</strong> {item.dietaryTags.join(', ')}
            </p>
            <p className={classes.muted}>{t('provisions.hints.dietary')}</p>
          </>
        ) : null}
        {medical ? (
          <Alert
            color="blue"
            variant="light"
            role="note"
            icon={<IconFirstAidKit size={16} />}
            className={css.note}
          >
            {t('provisions.hints.medical')}
          </Alert>
        ) : null}
        {chemical ? (
          <Alert
            color="blue"
            variant="light"
            role="note"
            icon={<IconFlask size={16} />}
            className={css.note}
          >
            {t('provisions.hints.chemical')}
          </Alert>
        ) : null}
        {item.notes ? <p className={css.notes}>{item.notes}</p> : null}
      </section>

      <section aria-label={t('provisions.sections.lots')}>
        <h3 className={css.detailTitle}>
          {t('provisions.sections.lots')}
          {expiryWarningDays ? (
            <span className={css.detailHint}>
              {k('warningWindow', { count: expiryWarningDays })}
            </span>
          ) : null}
        </h3>
        {failed ? (
          <EmptyState tone="danger" title={k('loadError')} />
        ) : !data ? (
          <Skeleton height={80} radius="sm" />
        ) : data.lots.length === 0 ? (
          <p className={classes.muted}>{onBoard ? t('provisions.empty.lots') : k('notOnBoard')}</p>
        ) : (
          <section
            className={css.scrollBox}
            aria-label={k('lotsTable')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.miniTable}>
              <thead>
                <tr>
                  <th scope="col">{t('provisions.columns.batch')}</th>
                  <th scope="col">{t('provisions.columns.expiry')}</th>
                  <th scope="col" data-numeric>
                    {t('provisions.columns.daysToExpiry')}
                  </th>
                  <th scope="col">{t('provisions.columns.received')}</th>
                  <th scope="col" data-numeric>
                    {t('provisions.columns.balance')}
                  </th>
                  <th scope="col">{t('provisions.columns.status')}</th>
                  {editor && can?.approve ? (
                    <th scope="col">
                      <span className={css.visuallyHidden}>{t('provisions.columns.actions')}</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {data.lots.map((lot) => (
                  <tr key={lot.id}>
                    <td>{lot.batchNo ?? '—'}</td>
                    <td>{lot.expiryDate ? formatDay(lot.expiryDate) : '—'}</td>
                    <td data-numeric>
                      {lot.daysToExpiry === null
                        ? '—'
                        : lot.daysToExpiry < 0
                          ? `−${Math.abs(lot.daysToExpiry)}`
                          : lot.daysToExpiry}
                    </td>
                    <td title={t(`provisions.lotSources.${lot.sourceType}`, { defaultValue: '' })}>
                      {formatDay(lot.receivedDate)}
                    </td>
                    <td data-numeric>{qty(lot.balance)}</td>
                    <td>
                      <StatusBadge tone={EXPIRY_TONE[lot.expiryState]}>
                        {t(`provisions.lotState.${lot.expiryState}`)}
                      </StatusBadge>
                    </td>
                    {editor && can?.approve ? (
                      <td>
                        {lot.expiryState === 'expired' ? (
                          <Button
                            size="compact-xs"
                            variant="subtle"
                            color="red"
                            onClick={() => onAction('dispose', item, lot.id)}
                          >
                            {t('provisions.actions.dispose')}
                          </Button>
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>

      <section aria-label={k('history')}>
        <h3 className={css.detailTitle}>{k('history')}</h3>
        {failed ? null : !data ? (
          <Skeleton height={80} radius="sm" />
        ) : data.movements.length === 0 ? (
          <p className={classes.muted}>{t('provisions.empty.movements')}</p>
        ) : (
          <section
            className={css.scrollBox}
            aria-label={k('historyTable')}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.miniTable}>
              <thead>
                <tr>
                  <th scope="col">{t('provisions.columns.date')}</th>
                  <th scope="col">{t('provisions.columns.type')}</th>
                  <th scope="col">{t('provisions.columns.batch')}</th>
                  <th scope="col" data-numeric>
                    {t('provisions.columns.qty')}
                  </th>
                  <th scope="col">{t('provisions.columns.reason')}</th>
                  <th scope="col">{t('provisions.columns.user')}</th>
                </tr>
              </thead>
              <tbody>
                {data.movements.map((move) => (
                  <tr key={move.id}>
                    <td>{formatDateTime(move.createdAt, move.tzOffsetMin)}</td>
                    <td>{t(`provisions.kinds.${move.kind}`, { defaultValue: move.kind })}</td>
                    <td>{move.batchNo ?? '—'}</td>
                    <td data-numeric>{`${formatSignedQuantity(move.quantity, locale)} ${unit}`}</td>
                    <td className={css.reason} title={move.reason ?? undefined}>
                      {move.reasonCode
                        ? `${t(`provisions.reasonCodes.${move.reasonCode}`, {
                            defaultValue: t(`provisions.disposalReasons.${move.reasonCode}`, {
                              defaultValue: move.reasonCode,
                            }),
                          })}${move.reason ? ` · ${move.reason}` : ''}`
                        : (move.reason ?? '—')}
                    </td>
                    <td>{move.createdByName ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </section>
    </div>
  );
}
